import type { ReactElement } from 'react';
import React, { useCallback, useMemo } from 'react';

import { WarningMessages } from 'constants/Warning.constants';
import { useFunctionSidebarContext } from 'contexts/FunctionSidebarContext';
import {
  setTestTargetFn,
  toggleFunctionTestPanel,
} from 'ducks/eventSourcingFunctionTestPanel/functionTestPanelSlice';
import { PATHS } from 'routes/routes.constants';
import { useStreamFunctions } from 'stream/globalFunction/useStreamFunctions';

import { useRouter } from '@polyai/common/hooks/useRouter';
import ConditionalWrapper from '@polyai/common/utils/conditionalWrapper';
import type { Flow } from '@polyai/stream/store/reducers/flows';
import { flowsSelectors } from '@polyai/stream/store/reducers/flows';
import { FunctionIcon, TalkTrackIcon } from '@polyai/ui/components/atoms/Icons';
import Tooltip from '@polyai/ui/components/atoms/Tooltip';
import { PopupMenuItem } from '@polyai/ui/components/molecules/PopupMenu';
import useIsOverflowing from '@polyai/ui/hooks/useIsOverflowing';

import { FUNCTION_PREFIX } from 'components/molecules/LexicalEditor/features/action/constants';
import type {
  ActionTagContentProps,
  ActionTypeInput,
  ActionTypeOutput,
} from 'components/molecules/LexicalEditor/features/action/types';
import { useActionsContext } from 'components/molecules/RichTextInputV2';
import { useAppSelector, useLocalDispatch } from 'hooks/reduxHooks';
import useProjectRoute from 'hooks/useProjectRoute';
import { generateNextPath } from 'lib/routeHelper';
import { useViewMode } from 'screens/withPermissions/useViewMode';
import * as Styled from './Function.styled';

const flowRegex = /conv\.goto_flow\(["']([^"']+)["']\)/g;

const findFlowReferences = (code?: string) => {
  if (!code) {
    return [];
  }

  const matches = [...code.matchAll(flowRegex)];

  return matches.map(m => m[1]);
};

const FunctionTagContent = ({
  id,
  closePopup,
  remove,
}: ActionTagContentProps) => {
  const dispatch = useLocalDispatch();
  const { pushUrl } = useRouter();
  const { openFunction } = useFunctionSidebarContext();
  const { activeFunctions } = useStreamFunctions();
  const functionCall = activeFunctions.find(f => f.id === id);
  const flows = useAppSelector(state =>
    flowsSelectors.selectAll(state.flows.flows),
  );

  const { isOverflow, targetRef } = useIsOverflowing();

  const { isEditable } = useViewMode();
  const { accountId, projectId } = useProjectRoute();

  const referencedFlows = useMemo(() => {
    const flowReferences = findFlowReferences(functionCall?.code);

    return flows?.filter(f => flowReferences.includes(f.name));
  }, [flows, functionCall?.code]);

  const handleTestFunction = () => {
    closePopup?.();
    dispatch(setTestTargetFn(functionCall!));
    dispatch(toggleFunctionTestPanel());
  };

  const openFlow = useCallback(
    (flow: Flow) => () => {
      pushUrl(
        generateNextPath(PATHS.FLOW_DETAILS, {
          accountId,
          projectId,
          flowId: flow.id,
        }),
      );
    },
    [accountId, projectId, pushUrl],
  );

  if (!functionCall) {
    return;
  }

  return (
    <React.Fragment>
      <PopupMenuItem
        data-test-id="go-to-function"
        onClick={() => {
          openFunction(functionCall.id, (_newName: string) => {
            // TODO: this should just invalidate/update the query cache actually?
            // update({ name: newName });
          });
          closePopup?.();
        }}
      >
        {isEditable ? 'Edit' : 'View'}
      </PopupMenuItem>

      <PopupMenuItem data-test-id="test-function" onClick={handleTestFunction}>
        Test
      </PopupMenuItem>

      {referencedFlows?.map(flow => (
        <PopupMenuItem
          key={flow.id}
          data-test-id={`goto-flow-${flow.name}`}
          onClick={openFlow(flow)}
        >
          <ConditionalWrapper
            condition={isOverflow}
            wrapper={children => (
              <Tooltip content={`Go to ${flow.name}`}>
                {children as ReactElement}
              </Tooltip>
            )}
          >
            <Styled.GoToFlow ref={targetRef}>Go to {flow.name}</Styled.GoToFlow>
          </ConditionalWrapper>
        </PopupMenuItem>
      ))}

      {isEditable && (
        <PopupMenuItem data-test-id="remove-function" isDelete onClick={remove}>
          Remove
        </PopupMenuItem>
      )}
    </React.Fragment>
  );
};

export const useFunctionActionType = ({
  id,
}: ActionTypeInput): ActionTypeOutput => {
  const { isFunctionsEnabled } = useActionsContext();
  const { allFunctions } = useStreamFunctions();

  const func = allFunctions.find(f => f.id === id);

  const isFlowFunction = func && !!findFlowReferences(func?.code).length;

  const warning =
    !func || func.archived
      ? WarningMessages.FUNCTION_NOT_EXIST
      : func.errors?.length
        ? WarningMessages.FUNCTION_HAS_ERRORS
        : undefined;

  return {
    Content: FunctionTagContent,
    name: func?.name ?? id,
    Icon: isFlowFunction ? TalkTrackIcon : FunctionIcon,
    iconColor: isFlowFunction ? 'iconInfo' : undefined,
    warning: warning,
    prefix: FUNCTION_PREFIX,
    isEnabled: isFunctionsEnabled,
    isLoading: false,
  };
};
